import { useQuery } from '@tanstack/react-query';
import { LearnableDetailsApi } from 'src/api';
import { LearnableDetails } from 'src/api/learnableDetails/types';
import { LearnablePronunciationData } from 'src/api/userDictionary/types';

/**
 * Shown when the session's first word has no native speaker clip of its own.
 */
export const GENERIC_INTRO_VIDEO_SRC =
  'https://static.memrise.com/uploads/items/videos/low/86649__f__1528476028_normalized.mp4';

/**
 * Resolves the native speaker video for the pronunciation intro screen: the first
 * session word's own clip where it has one, otherwise a generic intro clip.
 *
 * Returns undefined while the lookup is in flight so the caller can hold off
 * rendering rather than flashing the fallback and swapping it out.
 */
export const usePronunciationIntroVideo = (
  learnables: LearnablePronunciationData[],
): string | undefined => {
  const [firstLearnable] = learnables;

  const { data, isPending } = useQuery<LearnableDetails | null>({
    queryKey: ['learnableDetails', firstLearnable?.learnableIdKey],
    queryFn: () => LearnableDetailsApi.getLearnableDetails(firstLearnable.learnableIdKey),
    enabled: !!firstLearnable,
  });

  if (!firstLearnable) return GENERIC_INTRO_VIDEO_SRC;

  if (isPending) return undefined;

  return data?.videoUrls[0] ?? GENERIC_INTRO_VIDEO_SRC;
};
